import { useState } from 'react';
import type { PendingInvite } from '../../types';
import RoleBadge from '../common/RoleBadge';
import { useRevokeInvite, useResendInvite } from '../../hooks/useUsers';
import Spinner from '../common/Spinner';

interface Props {
  invite: PendingInvite;
  canRevoke: boolean;
}

export default function InviteRow({ invite, canRevoke }: Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const { mutate: revokeInvite, isPending: isRevoking } = useRevokeInvite();
  const { mutate: resendInvite, isPending: isResending } = useResendInvite();

  const expiresAt = invite.token_expires_at
    ? new Date(invite.token_expires_at)
    : null;
  const isExpired = expiresAt && expiresAt < new Date();

  const handleRevoke = () => {
    revokeInvite(invite.id, {
      onSuccess: () => setShowConfirm(false),
    });
  };

  const handleResend = () => {
    resendInvite(invite.id);
  };

  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <div className="font-medium text-gray-900">{invite.email}</div>
          <div className="text-sm text-gray-500">
            {isExpired ? (
              <span className="text-red-500">Expired</span>
            ) : expiresAt ? (
              `Expires ${expiresAt.toLocaleDateString()}`
            ) : (
              'Pending'
            )}
            {invite.invited_by_email && (
              <span className="ml-2">• Invited by {invite.invited_by_email}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <RoleBadge role={invite.role} />

        {canRevoke && (
          <div className="flex items-center gap-2">
            {showConfirm ? (
              <>
                <button
                  onClick={handleRevoke}
                  disabled={isRevoking}
                  className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  {isRevoking ? <Spinner size="sm" /> : 'Confirm'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleResend}
                  disabled={isResending}
                  className={`text-sm disabled:opacity-50 ${
                    isExpired
                      ? 'text-blue-600 hover:text-blue-700 font-medium'
                      : 'text-gray-600 hover:text-gray-700'
                  }`}
                >
                  {isResending ? <Spinner size="sm" /> : 'Resend'}
                </button>
                <button
                  onClick={() => setShowConfirm(true)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Revoke
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
